import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import Klaytn from 'klaytn/Klaytn';
import { RootState } from 'store/config';
import Caver from 'caver-js';
import { getUrlFromUnknownIPFSorHTTP } from 'utils/getIPFS';
import getJSON from 'utils/getJSON';
import CURRENT_CHAIN from 'constants/chainInfo';

export interface ContractState {
  isLoading: boolean;
  mintResult: {
    isDone: boolean;
    isError: boolean;
    tokenId: number;
    transactionHash: string;
    image: string;
    msg: string;
  };

  salesInfo: {
    name: string;
    symbol: string;
    mintIndexForSale: number;
    mintLimitPerBlock: number;
    mintLimitPerSale: number;
    mintStartBlockNumber: number;
    maxSaleAmount: number;
    mintPrice: number;
    isPublicMintEnabled: boolean;
  };
}

const initialState: ContractState = {
  isLoading: false,
  mintResult: {
    isDone: false,
    isError: false,
    tokenId: null,
    transactionHash: null,
    image: null,
    msg: null,
  },
  salesInfo: {
    name: '-',
    symbol: '-',
    mintIndexForSale: 0,
    mintLimitPerBlock: 0,
    mintLimitPerSale: 0,
    mintStartBlockNumber: 0,
    maxSaleAmount: 0,
    mintPrice: 0,
    isPublicMintEnabled: false,
  },
};

export const contractService = {
  connectContract: createAsyncThunk(
    'contract/connectContract',
    async (_, { rejectWithValue }) => {
      try {
        const salesInfo = await Klaytn.getSalesInfo();
        return { salesInfo };
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  ),

  publicMint: createAsyncThunk<any, number, { state: RootState }>(
    'contract/publicMint',
    async (mintAmount, { rejectWithValue }) => {
      try {
        const result = await Klaytn.PublicMint(mintAmount);
        const tokenId = result.events.Transfer.returnValues.tokenId;
        const transactionHash = result.transactionHash;
        const newSalesInfo = await Klaytn.getSalesInfo();

        const uri = getUrlFromUnknownIPFSorHTTP(
          await Klaytn.getTokenInfo(tokenId),
        );

        const { image } = await getJSON(uri);

        return { image, tokenId, transactionHash, newSalesInfo };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    },
  ),

  freeMint: createAsyncThunk<any, never, { state: RootState }>(
    'contract/freeMint',
    async (_, { rejectWithValue }) => {
      try {
        const result = await Klaytn.FreeMint();
        const tokenId = result.events.Transfer.returnValues.tokenId;
        const transactionHash = result.transactionHash;
        const newSalesInfo = await Klaytn.getSalesInfo();

        const uri = getUrlFromUnknownIPFSorHTTP(
          await Klaytn.getTokenInfo(tokenId),
        );

        const { image } = await getJSON(uri);

        return { tokenId, transactionHash, image, newSalesInfo };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    },
  ),

  publicMintWithEthers: createAsyncThunk<any, number, { state: RootState }>(
    'contract/publicMintWithEthers',
    async (mintAmount, { getState, rejectWithValue }) => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        await provider.send('eth_requestAccounts', []);
        const signer = provider.getSigner();
        const contract = await new ethers.Contract(
          CURRENT_CHAIN.contract.DEPLOYED_ADDRESS,
          CURRENT_CHAIN.contract.DEPLOYED_ABI,
          signer,
        );
        const { salesInfo } = getState().contract;
        const { mintPrice } = salesInfo;

        const output = await contract.publicMint(mintAmount, {
          value: String(Caver.utils.toPeb(mintPrice * mintAmount)),
        });

        const result = await output.wait();

        const tokenId = parseInt(result.logs[0].topics[3], 16);
        const transactionHash = result.transactionHash;

        const mintingInformation = await contract.mintingInformation();
        const name = await contract.name();
        const symbol = await contract.symbol();
        const isPublicMintEnabled = await contract.publicMintEnabled();

        const newSalesInfo = {
          name,
          symbol,
          mintIndexForSale: parseInt(mintingInformation[1]),
          mintLimitPerBlock: parseInt(mintingInformation[2]),
          mintLimitPerSale: parseInt(mintingInformation[3]),
          mintStartBlockNumber: parseInt(mintingInformation[4]),
          maxSaleAmount: parseInt(mintingInformation[5]),
          mintPrice: Caver.utils.fromPeb(
            parseInt(mintingInformation[6]),
            'KLAY',
          ),
          isPublicMintEnabled,
        };
        const uri = getUrlFromUnknownIPFSorHTTP(
          await Klaytn.getTokenInfo(tokenId),
        );

        const { image } = await getJSON(uri);

        return { image, newSalesInfo, tokenId, transactionHash };
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  ),

  freeMintWithEthers: createAsyncThunk<any, never, { state: RootState }>(
    'contract/freeMintWithEthers',
    async (_, { rejectWithValue }) => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        await provider.send('eth_requestAccounts', []);
        const signer = provider.getSigner();
        const contract = await new ethers.Contract(
          CURRENT_CHAIN.contract.DEPLOYED_ADDRESS,
          CURRENT_CHAIN.contract.DEPLOYED_ABI,
          signer,
        );

        const output = await contract.freeMint();
        const result = await output.wait();

        const tokenId = parseInt(result.logs[0].topics[3], 16);
        const transactionHash = result.transactionHash;

        const mintingInformation = await contract.mintingInformation();
        const name = await contract.name();
        const symbol = await contract.symbol();
        const isPublicMintEnabled = await contract.publicMintEnabled();

        const newSalesInfo = {
          name,
          symbol,
          mintIndexForSale: parseInt(mintingInformation[1]),
          mintLimitPerBlock: parseInt(mintingInformation[2]),
          mintLimitPerSale: parseInt(mintingInformation[3]),
          mintStartBlockNumber: parseInt(mintingInformation[4]),
          maxSaleAmount: parseInt(mintingInformation[5]),
          mintPrice: Caver.utils.fromPeb(
            parseInt(mintingInformation[6]),
            'KLAY',
          ),
          isPublicMintEnabled,
        };

        const uri = getUrlFromUnknownIPFSorHTTP(
          await Klaytn.getTokenInfo(tokenId),
        );

        const { image } = await getJSON(uri);

        return { image, newSalesInfo, tokenId, transactionHash };
      } catch (error) {
        return rejectWithValue(error);
      }
    },
  ),
};

const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(contractService.connectContract.pending, (state, action) => {
        state.isLoading = true;
        state.mintResult = initialState.mintResult;
      })
      .addCase(contractService.connectContract.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salesInfo = action.payload.salesInfo;
      })
      .addCase(contractService.connectContract.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(contractService.publicMint.pending, (state, action) => {
        state.isLoading = true;
        state.mintResult.isDone = false;
        state.mintResult.isError = false;
      })
      .addCase(contractService.publicMint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mintResult.isDone = true;
        state.salesInfo = action.payload.newSalesInfo;
        state.mintResult.tokenId = action.payload.tokenId;
        state.mintResult.transactionHash = action.payload.transactionHash;
        state.mintResult.image = action.payload.image;
        state.mintResult.msg = '민팅에 성공하였습니다.';
      })
      .addCase(contractService.publicMint.rejected, (state, action) => {
        state.isLoading = false;
        state.mintResult.isError = true;
        state.mintResult.msg = `민팅에 실패하였습니다.`;
      })
      .addCase(
        contractService.publicMintWithEthers.pending,
        (state, action) => {
          state.isLoading = true;
          state.mintResult.isDone = false;
          state.mintResult.isError = false;
        },
      )
      .addCase(
        contractService.publicMintWithEthers.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.mintResult.isDone = true;
          state.salesInfo = action.payload.newSalesInfo;
          state.mintResult.tokenId = action.payload.tokenId;
          state.mintResult.transactionHash = action.payload.transactionHash;
          state.mintResult.image = action.payload.image;
          state.mintResult.msg = '민팅에 성공하였습니다.';
        },
      )
      .addCase(
        contractService.publicMintWithEthers.rejected,
        (state, action) => {
          state.isLoading = false;
          state.mintResult.isError = true;
          state.mintResult.msg = `민팅에 실패하였습니다.`;
        },
      )
      .addCase(contractService.freeMint.pending, (state, action) => {
        state.isLoading = true;
        state.mintResult.isDone = false;
        state.mintResult.isError = false;
      })
      .addCase(contractService.freeMint.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mintResult.isDone = true;
        state.salesInfo = action.payload.newSalesInfo;
        state.mintResult.tokenId = action.payload.tokenId;
        state.mintResult.transactionHash = action.payload.transactionHash;
        state.mintResult.image = action.payload.image;
        state.mintResult.msg = '민팅에 성공하였습니다.';
      })
      .addCase(contractService.freeMint.rejected, (state, action) => {
        state.isLoading = false;
        state.mintResult.isError = true;
        state.mintResult.msg = `민팅에 실패하였습니다.`;
      })
      .addCase(contractService.freeMintWithEthers.pending, (state, action) => {
        state.isLoading = true;
        state.mintResult.isDone = false;
        state.mintResult.isError = false;
      })
      .addCase(
        contractService.freeMintWithEthers.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.mintResult.isDone = true;
          state.salesInfo = action.payload.newSalesInfo;
          state.mintResult.tokenId = action.payload.tokenId;
          state.mintResult.transactionHash = action.payload.transactionHash;
          state.mintResult.image = action.payload.image;
          state.mintResult.msg = '민팅에 성공하였습니다.';
        },
      )
      .addCase(contractService.freeMintWithEthers.rejected, (state, action) => {
        state.isLoading = false;
        state.mintResult.isError = true;
        state.mintResult.msg = `민팅에 실패하였습니다.`;
      });
  },
});

export const contractActions = contractSlice.actions;
export default contractSlice;
